/**
 * Invite panel (lane D1; INVENTORY-DESKTOP §4 row 11, prototype template lines
 * 655-666).
 *
 * NOT AN OVERLAY. It lives in `desktop/modals/` next to the six real modals
 * because that is where D0 filed it, but it renders INLINE inside the Friends
 * admin panel. `DesktopApp` deliberately does not mount it — `Crew` renders it
 * while `crewVM().hasInvite`, so this component does not re-check the gate.
 *
 * DEVIATION D-03 (inherited): this panel is the ONLY place the generated code
 * is ever shown. Nothing stores it — `crewVM().copyValue` on the admin rows
 * carries identity only — so dismissing the panel really does discard the code,
 * and RESET PW is the only way to get another one.
 *
 * D1-04 — two strings here are NOT the same as mobile's, despite §3.9's
 * "Invite panel — SAME strings": the desktop template says `COPY INVITE LINK`
 * (mobile `COPY LINK`) and appends `(they set their own on first login)` to the
 * temp-password line. Both are ported verbatim from the desktop source.
 */

import type { CSSProperties } from 'react';

import { useQd } from '../../store/index.ts';
import { copy } from '../../store/selectors.3b.ts';
import { crewVM } from '../../store/selectors.ts';

const CHIP: CSSProperties = {
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text-3)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '10px',
  padding: '7px 10px',
  cursor: 'pointer',
};

export default function InvitePanel(): JSX.Element {
  const s = useQd();
  const v = crewVM(s);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        border: '1px solid var(--acc)',
        background: 'var(--accSoft)',
        padding: '14px 16px',
      }}
    >
      <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '7.5px', color: 'var(--acc)' }}>
        INVITE READY — <span>{v.invName}</span>
      </div>
      {/* Twin of the phone panel's line. A display name is not a login — the
          server strips it to [a-z0-9] and bumps on collision — so handing over
          the name sent people to somebody else's account and refused them. */}
      <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--gt-text-2)' }}>
        SIGNS IN AS: <span style={{ color: 'var(--acc)' }}>{v.invKey}</span>
      </div>
      <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--gt-text-2)' }}>
        TEMP PASSWORD: <span style={{ color: 'var(--acc)' }}>{v.invPw}</span> (they set their own on first login)
      </div>
      <div
        style={{
          fontFamily: 'var(--gt-font-mono)',
          fontSize: '10px',
          color: 'var(--gt-text-5)',
          overflowWrap: 'anywhere',
        }}
      >
        <span>{v.invLink}</span>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <span onClick={() => copy(v.invLink, 'INVITE LINK COPIED')} style={CHIP}>
          COPY INVITE LINK
        </span>
        <span onClick={() => copy(v.invKey + ' / ' + v.invPw + ' — ' + v.invLink, 'CREDENTIALS COPIED')} style={CHIP}>
          COPY LOGIN + PASSWORD
        </span>
        <span onClick={s.closeInvite} style={{ ...CHIP, color: 'var(--gt-muted)' }}>
          DISMISS
        </span>
      </div>
    </div>
  );
}
